import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  attemptGetGuestPortalSettingsById,
  attemptGetLocationGuestPortalSettings,
  attemptGetBrandGuestPortalSettings,
  attemptUpdateLocationGuestPortalSettings,
  attemptUpdateBrandGuestPortalSettings,
  attemptGetGuestPortalSettingsBanner,
  attemptCreateGuestPortalSettingsBanner,
  attemptUpdateGuestPortalSettingsBanner,
  attemptGetGuestPortalSettingsLinks,
  attemptCreateGuestPortalSettingsLinks,
  attemptUpdateGuestPortalSettingsLinks,
  attemptDeleteGuestPortalSettingsLinks,
  attemptGetPlansGuestPortalSettings,
  attemptCreatePlanGuestPortalSettings,
  attemptUpdatePlanGuestPortalSettings,
  attemptDeletePlanGuestPortalSettings,
  attemptGetActiveLocationGuestPortalSettings,
  attemptGetGuestPortalPeriods,
  attemptCreateGuestPortalPeriod,
  attemptUpdateGuestPortalPeriod,
  attemptDeleteGuestPortalPeriod,
  attemptGetPeriodGuestPortalSettings,
  attemptUpdatePeriodGuestPortalSettings,
} from "api/OrgApi";

// Getting guest portal settings by ID
export const useGetGuestPortalSettingsById = (guestPortalId) => {
  return useQuery({
    queryKey: ["guest-portal-settings", guestPortalId],
    enabled: !!guestPortalId,
    queryFn: () => attemptGetGuestPortalSettingsById(guestPortalId),
  });
};

// Getting guest portal settings by location
export const useGetLocationGuestPortalSettings = (locationId, planId = null) => {
  return useQuery({
    queryKey: ["location-guest-portal-settings", locationId, planId],
    enabled: !!locationId || !!planId,
    queryFn: () => attemptGetLocationGuestPortalSettings(locationId, planId),
  });
};

// Getting guest portal settings by brand
export const useGetBrandGuestPortalSettings = (brandId, planId = null) => {
  return useQuery({
    queryKey: ["brand-guest-portal-settings", brandId, planId],
    enabled: !!brandId || !!planId,
    queryFn: () => attemptGetBrandGuestPortalSettings(brandId, planId),
  });
};

// Updating guest portal settings by location with optional brand support
export const useUpdateLocationGuestPortalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, formData, brandId = null, planId = null }) => attemptUpdateLocationGuestPortalSettings(locationId, formData, brandId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["location-guest-portal-settings", variables.locationId, variables.planId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["location-guest-portal-settings"] });
      }
    },
  });
};

// Updating guest portal settings by brand
export const useUpdateBrandGuestPortalSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ formData, brandId, planId = null }) => attemptUpdateBrandGuestPortalSettings(formData, brandId, planId),
    onSuccess: (_, variables) => {
      // Invalidate the specific brand's settings
      queryClient.invalidateQueries(["brand-guest-portal-settings", variables.brandId]);
    },
  });
};

// Banner-related hooks
export const useGetGuestPortalSettingsBanner = (guestPortalId) => {
  return useQuery({
    queryKey: ["guest-portal-settings-banner", guestPortalId],
    enabled: !!guestPortalId,
    queryFn: () => attemptGetGuestPortalSettingsBanner(guestPortalId),
  });
};

export const useCreateGuestPortalSettingsBanner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guestPortalId, formData, brandId = null, planId = null }) => {
      return attemptCreateGuestPortalSettingsBanner(guestPortalId, formData, brandId, planId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-settings-banner", variables.guestPortalId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["guest-portal-settings-banner"] });
      }
    },
  });
};

export const useUpdateGuestPortalSettingsBanner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guestPortalId, formData, brandId = null, planId = null }) => attemptUpdateGuestPortalSettingsBanner(guestPortalId, formData, brandId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-settings-banner", variables.guestPortalId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["guest-portal-settings-banner"] });
      }
    },
  });
};

// Links-related hooks
export const useGetGuestPortalSettingsLinks = (guestPortalId) => {
  return useQuery({
    queryKey: ["guest-portal-settings-links", guestPortalId],
    enabled: !!guestPortalId,
    queryFn: () => attemptGetGuestPortalSettingsLinks(guestPortalId),
  });
};

export const useCreateGuestPortalSettingsLinks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guestPortalId, formData, brandId = null, planId = null }) => attemptCreateGuestPortalSettingsLinks(guestPortalId, formData, brandId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-settings-links", variables.guestPortalId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["guest-portal-settings-links"] });
      }
    },
  });
};

export const useUpdateGuestPortalSettingsLinks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guestPortalId, formData, brandId = null, planId = null }) => attemptUpdateGuestPortalSettingsLinks(guestPortalId, formData, brandId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-settings-links", variables.guestPortalId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["guest-portal-settings-links"] });
      }
    },
  });
};

export const useDeleteGuestPortalSettingsLinks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guestPortalId, linkId, brandId = null, planId = null }) => attemptDeleteGuestPortalSettingsLinks(guestPortalId, linkId, brandId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-settings-links", variables.guestPortalId]);
      if (variables.brandId) {
        queryClient.invalidateQueries({ queryKey: ["guest-portal-settings-links"] });
      }
    },
  });
};

export const useGetPlansGuestPortalSettings = (brandId = null, locationId = null) => {
  return useQuery({
    queryKey: ["plans-guest-portal-settings", brandId, locationId],
    enabled: !!brandId || !!locationId,
    queryFn: () => attemptGetPlansGuestPortalSettings(brandId, locationId),
  });
};

export const useCreatePlanGuestPortalSettings = (formData) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formData }) => {
      return attemptCreatePlanGuestPortalSettings(formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["plans-guest-portal-settings", variables.formData.brandId, variables.formData.locationId]);
    },
  });
};

export const useUpdatePlanGuestPortalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, formData }) => {
      return attemptUpdatePlanGuestPortalSettings(planId, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["plans-guest-portal-settings"]);
    },
  });
};

export const useDeletePlanGuestPortalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, brand = null, location = null }) => {
      return attemptDeletePlanGuestPortalSettings(planId, brand, location);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["plans-guest-portal-settings"]);
    },
  });
};

export function useGetActiveLocationGuestPortalSettings(locationId) {
  return useQuery({
    queryKey: ["active-location-guest-portal-settings", locationId],
    enabled: !!locationId,
    queryFn: () => attemptGetActiveLocationGuestPortalSettings(locationId),
  });
}

// Guest portal periods hooks
export const useGetGuestPortalPeriods = (planId) => {
  return useQuery({
    queryKey: ["guest-portal-periods", planId],
    enabled: !!planId,
    queryFn: () => attemptGetGuestPortalPeriods(planId),
  });
};

export const useCreateGuestPortalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, formData }) => attemptCreateGuestPortalPeriod(planId, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-periods", variables.planId]);
    },
  });
};

export const useUpdateGuestPortalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ periodId, formData, planId }) => attemptUpdateGuestPortalPeriod(periodId, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-periods", variables.planId]);
    },
  });
};

export const useDeleteGuestPortalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ periodId, planId }) => attemptDeleteGuestPortalPeriod(periodId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["guest-portal-periods", variables.planId]);
    },
  });
};

export const useGetPeriodGuestPortalSettings = (periodId) => {
  return useQuery({
    queryKey: ["period-guest-portal-settings", periodId],
    enabled: !!periodId,
    queryFn: () => {
      // Use the guest portal setting ID from the period to get the full settings
      return attemptGetPeriodGuestPortalSettings(periodId);
    },
  });
};

export const useUpdatePeriodGuestPortalSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ periodId, formData }) => attemptUpdatePeriodGuestPortalSettings(periodId, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["period-guest-portal-settings", variables.periodId]);
    },
  });
};
